import { Button, Drawer, Group, Select, Stack, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateBuilding, useUpdateBuilding } from 'src/Hooks/Mutations/useCartographyMutations';
import { BuildingType } from 'src/types/cartography.types';

import { Props } from './BuildingDrawer.types';

const BUILDING_TYPES = Object.values(BuildingType);

const BuildingDrawer = ({ opened, building, onClose }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState<BuildingType | ''>('');

  const isEdit = !!building;

  useEffect(() => {
    if (opened) {
      setName(building?.name ?? '');
      setType(building?.type ?? '');
    }
  }, [opened, building]);

  const { mutate: createBuilding, isLoading: isCreating } = useCreateBuilding({
    onSuccess: onClose,
  });
  const { mutate: updateBuilding, isLoading: isUpdating } = useUpdateBuilding({
    onSuccess: onClose,
  });

  const handleSubmit = () => {
    if (!name.trim()) return;
    const payload = { name: name.trim(), type: type === '' ? undefined : type };
    if (isEdit && building) {
      updateBuilding({ buildingId: building.id, payload });
    } else {
      createBuilding(payload);
    }
  };

  const typeOptions = [
    { value: '', label: t('admin.structure.buildings.modal.typeNone', 'Non précisé') },
    ...BUILDING_TYPES.map(v => ({
      value: v,
      label: t(`admin.structure.buildingTypes.${v}`, v),
    })),
  ];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding={20}
      title={
        isEdit
          ? t('admin.structure.buildings.modal.editTitle', 'Modifier le bâtiment')
          : t('admin.structure.buildings.modal.createTitle', 'Nouveau bâtiment')
      }
    >
      <Stack gap="md">
        <TextInput
          label={t('admin.structure.buildings.modal.name', 'Nom')}
          placeholder={t('admin.structure.buildings.modal.namePlaceholder', 'Maison principale')}
          value={name}
          onChange={e => setName(e.currentTarget.value)}
          required
          data-autofocus
        />
        <Select
          label={t('admin.structure.buildings.modal.type', 'Type')}
          data={typeOptions}
          value={type}
          onChange={value => setType((value as BuildingType) ?? '')}
        />
        <Group justify="space-between">
          <Button variant="outline" onClick={onClose}>
            {t('admin.structure.buildings.modal.cancel', 'Annuler')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isCreating || isUpdating}
            disabled={!name.trim()}
          >
            {t('admin.structure.buildings.modal.save', 'Enregistrer')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
};

export default BuildingDrawer;
